import { useState } from "react"
import styled from "styled-components"

import { spacing, Text, TextArea, TextField } from "@minuthq/meatball-ui-react"

import { ContextMeta } from "src/components/MinutAI/MinutAIKnowledge/MinutAIContentLibrary/ContextMeta"
import { ExpandableContextGuidelines } from "src/components/MinutAI/MinutAIKnowledge/MinutAIContentLibrary/ExpandableContextGuidelines"
import {
  TContextTarget,
  TEnhancedPropertyContext,
} from "src/components/MinutAI/minutAITypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  usePatchPropertyContext,
  usePostPropertyContext,
} from "src/data/propertyContext/propertyContextQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { MDialog } from "src/ui/Dialog/MDialog"
import { Divider } from "src/ui/Divider/Divider"
import { textCharacterCount, textWordCount } from "src/utils/genericUtil"

import { ContextTargetSelect } from "./ContextTargetSelect"

const FORM_ID = "create-context-form"

const MAX_CHARACTERS = 5000

export function MutateContextDialog({
  onClose,
  context,
}: {
  onClose: () => void
  context?: TEnhancedPropertyContext
}) {
  const { tPlaceholder, t, langKeys } = useTranslate()
  const { org } = useOrganization()

  const [inputTitle, setInputTitle] = useState(context?.title || "")
  const [inputContext, setInputContext] = useState(context?.content || "")
  const [contextTarget, setContextTarget] = useState<TContextTarget | null>(
    () => {
      if (context) {
        return initialContextTarget(context)
      }
      return null
    }
  )

  const isUpdating = !!context

  const postPropertyContext = usePostPropertyContext()
  const patchPropertyContext = usePatchPropertyContext()

  function handleSubmit() {
    const target = getBodyTarget(contextTarget)

    if (isUpdating) {
      patchPropertyContext.mutate(
        {
          path: {
            organization_id: org.id,
            property_context_id: context.id,
          },
          body: {
            title: inputTitle.trim(),
            content: inputContext.trim(),
            ...target,
          },
        },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )

      return
    }

    postPropertyContext.mutate(
      {
        path: {
          organization_id: org.id,
        },
        body: {
          title: inputTitle.trim(),
          content: inputContext.trim(),
          ...target,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  const contextWordCount = textWordCount(inputContext)
  const contextCharacterCount = textCharacterCount(inputContext)

  return (
    <>
      <MDialog
        open={true}
        onClose={onClose}
        title={
          context ? tPlaceholder("Edit content") : tPlaceholder("Add content")
        }
        description={tPlaceholder(
          "This could be a documentation, FAQs, procedures, or any text-based information that you want Minut AI to learn."
        )}
        confirmLabel={tPlaceholder("Save")}
        closeLabel={t(langKeys.cancel)}
        confirmButtonProps={{
          loading:
            postPropertyContext.isPending || patchPropertyContext.isPending,
        }}
        formId={FORM_ID}
        error={
          postPropertyContext.error
            ? t(langKeys.failed_contact_support)
            : undefined
        }
      >
        <Form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <FieldSet>
            <TextField
              placeholder={tPlaceholder("Title (e.g. House rules unit 01)")}
              value={inputTitle}
              onChange={setInputTitle}
              required
            />

            <TextAreaWrapper>
              <TextArea
                placeholder={tPlaceholder("Add information here...")}
                value={inputContext}
                onChange={setInputContext}
                required
                minRows={6}
              />
              <Text variant="bodyXS" color="tertiary" textAlign="right">
                {tPlaceholder(`${contextWordCount} words`)} &bull;{" "}
                {tPlaceholder(
                  `${contextCharacterCount} / ${MAX_CHARACTERS} characters`
                )}
              </Text>
            </TextAreaWrapper>
          </FieldSet>

          <FieldSet>
            <Text variant="subtitleS" color="secondary">
              {tPlaceholder(
                "Apply to a specific unit, building, or location? Add it below."
              )}
            </Text>

            <ContextTargetSelect
              selectedTarget={contextTarget}
              onChangeTarget={setContextTarget}
            />
          </FieldSet>
        </Form>

        {!isUpdating ? (
          <ExpandableContextGuidelines />
        ) : (
          <div>
            <Divider $margin={spacing.M} />
            <ContextMeta context={context} />
            <Divider $margin={spacing.M} />
          </div>
        )}
      </MDialog>
    </>
  )
}

function initialContextTarget(
  context: TEnhancedPropertyContext
): TContextTarget | null {
  if (context.home) {
    return {
      type: "home",
      id: context.home.home_id,
      name: context.home.name || "",
    }
  }

  if (context.homegroup) {
    return {
      type: "homegroup",
      id: context.homegroup.id,
      name: context.homegroup.name,
    }
  }

  return null
}

function getBodyTarget(target: TContextTarget | null): {
  home_id?: string
  homegroup_id?: string
} {
  if (target?.type === "home") {
    return {
      home_id: target.id,
      homegroup_id: "",
    }
  }

  if (target?.type === "homegroup") {
    return {
      homegroup_id: target.id,
      home_id: "",
    }
  }

  return {
    home_id: "",
    homegroup_id: "",
  }
}

const Form = styled.form`
  display: grid;
  gap: ${spacing.M};
  margin-bottom: ${spacing.L};
`

const FieldSet = styled.div`
  display: grid;
  gap: ${spacing.XS};
`

const TextAreaWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS2};
`
